import React, { useEffect, useMemo } from "react";
import { MainLayout } from "../../layout/mainLayout";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { MuiDataGrid } from "../../framework/muiDataGrid";
import { GridColDef } from "@mui/x-data-grid-premium";
import { t } from "i18next";

import { fetchAllVehicleGroups, fetchVehicleOptions } from "../../../store/vehicleSlice";
import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { fetchGroupedCostCenterOptions } from "../../../store/costCenterSlice";
import { FuelType } from "../../../models/vehicle/fuelType";
import moment from "moment";

export const VehiclesView = () => {
    return (
        <MainLayout>
            <VehiclesViewContent />
        </MainLayout>
    );
};

export interface VehicleDetailsListItem extends VehicleListItemDto {
    vehicleGroupName: string;
    costCenterName: string;
    fuelTypeName: string;
}

export const VehiclesViewContent = () => {
    const { vehicles, allVehicleGroups } = useAppSelector((state) => state.vehicle);
    const costCenters = useAppSelector((state) => state.costCenter.groupedOptions);
    const dispatch = useAppDispatch();

    const gridDef: GridColDef<VehicleDetailsListItem>[] = useMemo(
        () => [
            {
                headerName: t("common.vehicleNumber"),
                hideable: false,
                field: "registerNumber",
                flex: 1,
                type: "string",
            },
            {
                headerName: t("vehicle.shortNumber"),
                hideable: true,
                field: "number",
                type: "int",
                flex: 1,
            },
            {
                headerName: t("vehicle.abbreviation"),
                hideable: true,
                field: "abbreviation",
                flex: 1,
                type: "string",
            },
            {
                headerName: t("common.vehicleName"),
                hideable: true,
                field: "brand",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.vehicleGroup"),
                hideable: true,
                field: "vehicleGroupName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.costCenter"),
                hideable: true,
                field: "costCenterName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("common.fuelTypes.fuelTypeTitle"),
                hideable: true,
                field: "fuelTypeName",
                type: "string",
                flex: 2,
            },
            {
                headerName: t("vehicle.inspectionDate"),
                hideable: true,
                field: "inspectionDate",
                type: "string",
                flex: 2,
                valueFormatter: params => {
                    return params?.value ? moment(params?.value as string).format("DD/MM/YYYY") : "";
                }
            }
        ],
        []
    );

    const getVehicleGroupName = (vehicleGroupId?: string) => {
        return allVehicleGroups?.find((vg) => vg.id === vehicleGroupId)?.name ?? "";
    };

    const getCostCenterName = (costCenterId?: string) => {
        return costCenters?.find((vg) => vg.value === costCenterId)?.label ?? "";
    };

    // Load related data to state if not loaded yet
    useEffect(() => {

        // Load vehicles when view is opened to get fresh data to the list
        dispatch(fetchVehicleOptions() as any);

        if (!allVehicleGroups || allVehicleGroups.length === 0) {
            dispatch(fetchAllVehicleGroups([]) as any);
        }
        if (!costCenters || costCenters.length === 0) {
            dispatch(fetchGroupedCostCenterOptions() as any);
        }
    }, []);

    const visibleVehicles = useMemo(() => {
        return (vehicles ?? [] as VehicleListItemDto[]).map((v) => {

            return {
                id: v.id,
                registerNumber: v.registerNumber,
                brand: v.brand,
                vehicleGroupId: v.vehicleGroupId,
                costCenterId: v.costCenterId,
                fuelType: v.fuelType,
                number: v.number,
                comment: v.comment,
                inspectionDate: v.inspectionDate,
                abbreviation: v.abbreviation,
                fuelTypeName: t(`common.fuelTypes.fuelType.${FuelType[v.fuelType]}`),
                vehicleGroupName: getVehicleGroupName(v.vehicleGroupId),
                costCenterName: getCostCenterName(v.costCenterId)
            };
        }
        ) as VehicleDetailsListItem[];
    }, [vehicles]);

    return (
        <MuiDataGrid
            title={t("fleetManagementView.vehicles.listTitle")}
            columns={gridDef}
            rows={visibleVehicles}
            hideFooter
        />
    );
};